<template>
  <div class="year-table">
    <PrintHeader
      :title="`${year} Holiday Schedule`"
      :partyAName="partyAName"
      :partyBName="partyBName"
      :subtitle="jurisdictionLabel"
    />

    <p v-if="!groups.length" class="empty-state">
      No arrangements decided yet for {{ year }}. Configure holidays on the left to populate this view.
    </p>

    <table v-else class="single-table">
      <thead>
        <tr>
          <th class="col-name">Holiday</th>
          <th class="col-arr">Arrangement</th>
          <th class="col-who">In the care of</th>
        </tr>
      </thead>
      <tbody v-for="cat in groups" :key="cat.key">
        <tr class="cat-row">
          <td colspan="3">{{ cat.label }}</td>
        </tr>
        <tr v-for="h in cat.list" :key="h.id" :class="rowClass(h)">
          <td class="td-name">{{ h.name }}</td>
          <td class="td-arr">
            <div class="arr-cell">
              <span class="arr-label">{{ arrangementLabel(h) }}</span>
              <span v-if="isLongWeekendScope(h)" class="lw-tag">LW</span>
            </div>
          </td>
          <td class="td-who">
            <!-- When the user has overridden the prose with custom wording,
                 render that directly (matches the PDF). Otherwise the
                 year-specific "who" answer + timing + notes stacked. -->
            <template v-if="h.customProse && h.customProse.trim()">
              <span class="care-who care-custom">{{ h.customProse.trim() }}</span>
            </template>
            <template v-else>
              <span :class="['care-who', whoChipClass(h)]">{{ whoForYear(h) }}</span>
              <span v-if="timesLabel(h)" class="care-time">{{ timesLabel(h) }}</span>
              <span v-if="noteText(h)" class="care-note">{{ noteText(h) }}</span>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { CATEGORIES } from '@/lib/parentingSchedule/seed/holidays'
import {
  parentForYear,
  formatTimeOfDay,
  CATEGORY_ORDER,
  UNDECIDED_KINDS,
  arrangementLabel,
  isLongWeekendScope,
  noteText,
} from '@/lib/parentingSchedule/proseRenderer'
import PrintHeader from '../../PrintHeader.vue'
import { PROVINCE_NAMES } from '@/lib/holidayScheduleProvince'


export default {
  name: 'YearTable',
  components: { PrintHeader },
  props: {
    holidays: { type: Array, required: true },
    partyAName: { type: String, default: '' },
    partyBName: { type: String, default: '' },
    jurisdiction: { type: String, default: '' },
    children: { type: Array, default: () => [] },
    year: { type: Number, required: true },
  },
  computed: {
    jurisdictionLabel() {
      return PROVINCE_NAMES[this.jurisdiction] || this.jurisdiction
    },
    groups() {
      const byCategory = {}
      for (const h of this.holidays) {
        if (UNDECIDED_KINDS.has(h?.rule?.kind)) continue
        ;(byCategory[h.category] ||= []).push(h)
      }
      return CATEGORY_ORDER
        .filter((k) => byCategory[k]?.length)
        .map((k) => ({ key: k, label: CATEGORIES[k], list: byCategory[k] }))
    },
  },
  methods: {
    arrangementLabel,
    isLongWeekendScope,
    noteText,
    // Short label for the rule kind — same set of strings as AgreementTable's
    // Arrangement column. Reused on screen here so the two tables read the
    // same way (Holiday · Arrangement · In the care of).
    // Combined timing label for the "In the care of" cell. "Times to be
    // agreed by the parties" overrides pickup/dropoff; paired-day-split's
    // structural splitTime is unaffected. Always renders in AM/PM otherwise.
    timesLabel(h) {
      if (h.timesByAgreement && h.rule.kind !== 'paired-day-split') {
        return 'At times to be agreed by the parties'
      }
      if (h.rule.kind === 'paired-day-split') {
        const t = formatTimeOfDay(h.rule.splitTime || '12:00')
        return `${t} → ${t}`
      }
      const p = formatTimeOfDay(h.pickup)
      const d = formatTimeOfDay(h.dropoff)
      if (p && d) return `${p} → ${d}`
      if (p)      return `From ${p}`
      if (d)      return `Until ${d}`
      return ''
    },
    // Free-form note text. customProse renders separately as the main
    // "who" line now, so this only carries the user's plain notes.
    whoForYear(h) {
      const who = parentForYear(h.rule, this.year)
      const A = this.partyAName || 'Party A'
      const B = this.partyBName || 'Party B'
      const nameOf = (p) => (p === 'A' ? A : p === 'B' ? B : '')
      if (who === 'A') return A
      if (who === 'B') return B
      if (who === 'shared') return `${A} and ${B} together`
      if (who === 'split') {
        const first = nameOf(h.rule.firstHalfParent)
        const second = nameOf(h.rule.firstHalfParent === 'A' ? 'B' : 'A')
        if (h.rule.kind === 'split-period') {
          return `${first} for the first half, ${second} for the second`
        }
        if (h.rule.transitionByAgreement) {
          return `${first} for the first part, then ${second} — transition time agreed by the parties`
        }
        const t = formatTimeOfDay(h.rule.transitionTime || '12:00')
        return `${first} until ${t}, then ${second}`
      }
      if (who === 'rotation') {
        const start = nameOf(h.rule.startParent)
        const other = nameOf(h.rule.startParent === 'A' ? 'B' : 'A')
        return `${start} the first week, alternating with ${other} thereafter`
      }
      if (who === 'base') return 'Per the regular parenting schedule'
      if (who === 'flex') return 'Decide together, with reasonable notice'
      return 'Needs decision'
    },
    rowClass(h) {
      const who = parentForYear(h.rule, this.year)
      if (who == null) return 'pending'
      if (who === 'flex' || who === 'base') return 'soft'
      return ''
    },
    whoChipClass(h) {
      const who = parentForYear(h.rule, this.year)
      if (who === 'A') return 'who-chip chip-a'
      if (who === 'B') return 'who-chip chip-b'
      if (who === 'shared' || who === 'split' || who === 'rotation') return 'who-chip chip-share'
      return 'who-text'
    },
  },
}
</script>

<style scoped>
.year-table {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.25rem 1.4rem;
  font-size: 0.85rem;
  color: #1e293b;
}

.empty-state {
  margin: 1rem 0;
  padding: 1rem;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  text-align: center;
  color: #64748b;
  font-size: 0.85rem;
  font-style: italic;
}

.single-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}
.single-table th, .single-table td {
  text-align: left;
  padding: 0.45rem 0.55rem;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
  /* Wrap by default on screen; print rules below force nowrap. */
  white-space: normal;
}
.single-table th {
  background: #244047;
  color: #fff;
  font-weight: 700;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.5rem 0.55rem;
  vertical-align: middle;
}

.td-arr { color: #475569; }

/* Arrangement cell layout: label on the left, LW pill (when present)
   right-aligned so the pills line up consistently across all rows. */
.arr-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
}
.arr-label { flex-grow: 1; }
.lw-tag {
  display: inline-block;
  flex-shrink: 0;
  padding: 0.05rem 0.4rem;
  background: #fde9bf;
  color: #85581a;
  border-radius: 4px;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  vertical-align: middle;
}

/* Category rows: Divii dark-teal banner pattern */
tr.cat-row td {
  background: #eef3f4;
  color: #34555f;
  font-weight: 700;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.4rem 0.5rem;
  border-bottom: 1px solid #cbd5e1;
}

.td-name { font-weight: 500; }
tr.pending td { color: #94a3b8; font-style: italic; }
tr.pending .td-name { color: #1e293b; font-style: normal; font-weight: 500; }
tr.soft td { color: #475569; }
tr.soft .who-text { font-style: italic; }

/* Combined "In the care of" cell. Each stack-line is display:block so
   the year-specific who, the times, and any free-form note appear on
   their own lines inside one cell. */
.care-who {
  display: block;
  font-weight: 500;
  font-size: 0.82rem;
  line-height: 1.4;
}
.care-time {
  display: block;
  font-size: 0.78rem;
  color: #475569;
  font-variant-numeric: tabular-nums;
  margin-top: 0.15rem;
}
.care-note {
  display: block;
  font-size: 0.78rem;
  color: #64748b;
  font-style: italic;
  margin-top: 0.1rem;
}
.chip-a { color: #244047; font-weight: 600; }
.chip-b { color: #85581a; font-weight: 600; }
.chip-share { color: #34555f; }
.who-text { color: #64748b; font-style: italic; font-weight: 400; }

/* Three columns; "In the care of" gets the most room. */
.col-name  { width: 22%; }
.col-arr   { width: 22%; }
.col-who   { width: 56%; }

/* Print: tighten everything so 24 holidays + header + section bands fit on
   one US-letter page. Single-page fridge format. */
@media print {
  .year-table {
    border: none;
    border-radius: 0;
    padding: 0;
    box-shadow: none;
    overflow: visible;
    page-break-inside: avoid;
  }
  .single-table { font-size: 0.62rem; line-height: 1.3; page-break-inside: avoid; }
  /* Three columns, same as on screen. "In the care of" gets the lion's share. */
  .col-name  { width: 22%; }
  .col-arr   { width: 22%; }
  .col-who   { width: 56%; }
  .single-table th, .single-table td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0.16rem 0.34rem;
    vertical-align: top;
  }
  .single-table td.td-who { white-space: normal; }
  /* Force the dark teal background + white text to render in the printed
     PDF (browsers strip color backgrounds in print by default). */
  .single-table th {
    padding: 0.3rem 0.4rem;
    font-size: 0.66rem;
    background: #244047 !important;
    color: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  tr.cat-row td {
    padding: 0.22rem 0.34rem;
    font-size: 0.62rem;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .care-who { font-size: 0.62rem; line-height: 1.3; }
  .care-time { font-size: 0.58rem; }
  .care-note { font-size: 0.58rem; }
  tr { page-break-inside: avoid; }
}
</style>
